import React, { useEffect, useState } from "react";
import { Card, Tabs, Table, Statistic, Row, Col, DatePicker, Space, Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import moment from "moment";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const CompanyDataView = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [stock, setStock] = useState([]);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [activeTab, setActiveTab] = useState("stock");

  useEffect(() => {
    fetchCompanyInfo();
    fetchDashboardData();
    fetchStock(); // Load stock data initially
  }, [companyId]);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`super-admin/companies/${companyId}`);
      setCompany(response.data);
    } catch (error) {
      toast.error("Failed to fetch company information");
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`super-admin/companies/${companyId}/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchStock = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`super-admin/companies/${companyId}/stock`);
      setStock(response.data || []);
    } catch (error) {
      console.error("Failed to fetch stock:", error);
      toast.error("Failed to fetch stock data");
      setStock([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSales = async (dates = null) => {
    setLoading(true);
    try {
      const params = {};
      const datesToUse = dates || dateRange;
      if (datesToUse && datesToUse.length === 2 && datesToUse[0] && datesToUse[1]) {
        params.startDate = moment(datesToUse[0]).format("YYYY-MM-DD");
        params.endDate = moment(datesToUse[1]).format("YYYY-MM-DD");
      }
      const response = await axios.get(`super-admin/companies/${companyId}/sales`, { params });
      setSales(response.data || []);
    } catch (error) {
      console.error("Failed to fetch sales:", error);
      toast.error("Failed to fetch sales data");
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchases = async (dates = null) => {
    setLoading(true);
    try {
      const params = {};
      const datesToUse = dates || dateRange;
      if (datesToUse && datesToUse.length === 2 && datesToUse[0] && datesToUse[1]) {
        params.startDate = moment(datesToUse[0]).format("YYYY-MM-DD");
        params.endDate = moment(datesToUse[1]).format("YYYY-MM-DD");
      }
      const response = await axios.get(`super-admin/companies/${companyId}/purchases`, { params });
      setPurchases(response.data || []);
    } catch (error) {
      console.error("Failed to fetch purchases:", error);
      toast.error("Failed to fetch purchases data");
      setPurchases([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`super-admin/companies/${companyId}/customers`);
      setCustomers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      toast.error("Failed to fetch customers");
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`super-admin/companies/${companyId}/suppliers`);
      setSuppliers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
      toast.error("Failed to fetch suppliers");
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const stockColumns = [
    { title: "Product Name", dataIndex: ["product", "name"], key: "name" },
    { title: "ISBN", dataIndex: ["product", "isbn"], key: "isbn" },
    { title: "Quantity", dataIndex: "quantity", key: "quantity" },
    { title: "Reorder Quantity", dataIndex: "reorder_quantity", key: "reorder_quantity" },
  ];

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "₹0.00";
    return `₹${parseFloat(value).toFixed(2)}`;
  };

  const salesColumns = [
    { title: "Date", dataIndex: "date", key: "date", render: (date) => moment(date).format("YYYY-MM-DD") },
    { title: "Invoice Number", dataIndex: "invoice_number", key: "invoice_number" },
    { title: "Customer", dataIndex: ["customer", "name"], key: "customer" },
    { title: "Total Amount", dataIndex: "total_amount", key: "total_amount", render: formatCurrency },
    { title: "Paid Amount", dataIndex: "paid_amount", key: "paid_amount", render: formatCurrency },
    { title: "Due Amount", dataIndex: "due_amount", key: "due_amount", render: formatCurrency },
  ];

  const purchaseColumns = [
    { title: "Date", dataIndex: "date", key: "date", render: (date) => moment(date).format("YYYY-MM-DD") },
    { title: "Supplier", dataIndex: ["supplier", "name"], key: "supplier" },
    { title: "Total Amount", dataIndex: "total_amount", key: "total_amount", render: formatCurrency },
    { title: "Paid Amount", dataIndex: "paid_amount", key: "paid_amount", render: formatCurrency },
    { title: "Due Amount", dataIndex: "due_amount", key: "due_amount", render: formatCurrency },
  ];

  const customerColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Phone", dataIndex: "phone", key: "phone" },
    { title: "Address", dataIndex: "address", key: "address" },
    { title: "Total Invoices", dataIndex: ["_count", "saleInvoice"], key: "invoices" },
    { title: "Status", dataIndex: "status", key: "status", render: (status) => (status ? "Active" : "Inactive") },
  ];

  const supplierColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Phone", dataIndex: "phone", key: "phone" },
    { title: "Address", dataIndex: "address", key: "address" },
    { title: "Total Invoices", dataIndex: ["_count", "purchaseInvoice"], key: "invoices" },
    { title: "Status", dataIndex: "status", key: "status", render: (status) => (status ? "Active" : "Inactive") },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate("/super-admin/companies")}
        style={{ marginBottom: "16px" }}
      >
        Back to Companies
      </Button>
      <Card title={company?.company_name || "Company Data"} loading={loading}>
        {dashboardData && (
          <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Total Customers" value={dashboardData.totalCustomers} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Total Suppliers" value={dashboardData.totalSuppliers} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic 
                title="Total Sales" 
                value={dashboardData.totalSales} 
                prefix="₹" 
                precision={2}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic 
                title="Total Purchases" 
                value={dashboardData.totalPurchases} 
                prefix="₹" 
                precision={2}
                valueStyle={{ color: '#cf1322' }}
              />
            </Col>
          </Row>
        )}

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            if (key === "stock") {
              fetchStock();
            } else if (key === "sales") {
              fetchSales();
            } else if (key === "purchases") {
              fetchPurchases();
            } else if (key === "customers") {
              fetchCustomers();
            } else if (key === "suppliers") {
              fetchSuppliers();
            }
          }}
        >
          <TabPane tab="Stock" key="stock">
            <Table dataSource={stock} columns={stockColumns} rowKey="id" loading={loading} />
          </TabPane>
          <TabPane tab="Sales" key="sales">
            <Space style={{ marginBottom: 16 }}>
              <RangePicker 
                value={dateRange}
                onChange={(dates) => {
                  setDateRange(dates);
                  fetchSales(dates);
                }} 
              />
              <Button onClick={() => fetchSales()}>Refresh</Button>
            </Space>
            <Table dataSource={sales} columns={salesColumns} rowKey="id" loading={loading} />
          </TabPane>
          <TabPane tab="Purchases" key="purchases">
            <Space style={{ marginBottom: 16 }}>
              <RangePicker 
                value={dateRange}
                onChange={(dates) => {
                  setDateRange(dates);
                  fetchPurchases(dates);
                }} 
              />
              <Button onClick={() => fetchPurchases()}>Refresh</Button>
            </Space>
            <Table dataSource={purchases} columns={purchaseColumns} rowKey="id" loading={loading} />
          </TabPane>
          <TabPane tab="Customers" key="customers">
            <Table dataSource={customers} columns={customerColumns} rowKey="id" loading={loading} />
          </TabPane>
          <TabPane tab="Suppliers" key="suppliers">
            <Table dataSource={suppliers} columns={supplierColumns} rowKey="id" loading={loading} />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default CompanyDataView;
